import { Link } from "react-router-dom";
import {
  ArrowRight, Buildings, Handshake, ChartLine, ShieldCheck,
  Lightning, CaretLeft, CaretRight, Quotes, Trophy, Users, CurrencyDollar
} from "@phosphor-icons/react";
import { individualServices, businessServices } from "@/data/servicesData";
import { blogPosts } from "@/data/blogData";
import { useState, useEffect, useRef } from "react";
import SEO, { organizationSchema, websiteSchema } from "@/components/SEO";

const HERO_BG = "https://images.unsplash.com/photo-1772631653221-9c77e6f82c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzc0OTM5NjYxfDA&ixlib=rb-4.1.0&q=85";

const metrics = [
  { icon: Users, value: "140+", label: "Clients Advised", suffix: "" },
  { icon: CurrencyDollar, value: "$36M+", label: "In Assets Advised", suffix: "" },
  { icon: Trophy, value: "4", label: "Industry Awards", suffix: "" },
  { icon: Buildings, value: "100%", label: "Crypto-Focused", suffix: "" },
];

const trustSignals = [
  { icon: ShieldCheck, label: "FIU-IND Expertise" },
  { icon: ChartLine, label: "Portfolio Advisory" },
  { icon: Buildings, label: "Enterprise Consulting" },
  { icon: Handshake, label: "Trusted Partners" },
  { icon: Lightning, label: "Fast Onboarding" },
];

const howItWorks = [
  {
    step: "01",
    title: "Discovery Call",
    desc: "We start by understanding your goals, challenges, and current situation. Whether you are an individual crypto investor or a business exploring blockchain, the first step is always listening."
  },
  {
    step: "02",
    title: "Strategy & Roadmap",
    desc: "We design a tailored plan for your needs. This could be a crypto investment strategy, a regulatory compliance roadmap, a blockchain architecture plan, or a team hiring blueprint."
  },
  {
    step: "03",
    title: "Execution & Support",
    desc: "We work alongside you to implement the plan, providing ongoing advisory, regular reviews, and proactive adjustments as the market and your needs evolve."
  }
];

const testimonials = [
  {
    name: "Arjun Mehta",
    role: "Crypto Investor, Mumbai",
    text: "BitGenix brought structure to my crypto portfolio. I was making emotional trades for years. Their advisory helped me build a real investment strategy and finally understand my crypto tax obligations under Section 115BBH."
  },
  {
    name: "Priya Krishnan",
    role: "Founder, DeFi Protocol",
    text: "We needed FIU-IND registration guidance and a development partner who understood blockchain. BitGenix delivered both. Their regulatory knowledge saved us months of costly back-and-forth with compliance authorities."
  },
  {
    name: "Rajesh Gupta",
    role: "CFO, Fintech Company",
    text: "The team at BitGenix helped us navigate FIU-IND registration and build our PMLA compliance framework from scratch. Professional, thorough, and genuinely knowledgeable about crypto regulation in India."
  },
  {
    name: "Sneha Reddy",
    role: "NRI Investor, Dubai",
    text: "As an NRI, my crypto tax situation was incredibly complex across multiple jurisdictions. BitGenix simplified everything and helped me develop a compliant strategy that actually optimized my overall tax position."
  }
];

function AnimatedCounter({ value, label }) {
  const [displayed, setDisplayed] = useState("0");
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const numMatch = value.match(/[\d.]+/);
          if (!numMatch) { setDisplayed(value); return; }
          const target = parseFloat(numMatch[0]);
          const prefix = value.slice(0, value.indexOf(numMatch[0]));
          const suffix = value.slice(value.indexOf(numMatch[0]) + numMatch[0].length);
          const duration = 1500;
          const startTime = performance.now();
          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * target);
            setDisplayed(`${prefix}${current}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
            else setDisplayed(value);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="font-heading font-semibold text-4xl sm:text-5xl text-[#A7A55C] tracking-tight">
      {displayed}
    </span>
  );
}

function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="BitGenix crypto consultancy office"
          className="w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#21324D] via-[#21324D]/95 to-[#21324D]/60" />
      </div>
      {/* Decorative grid lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-[20%] w-px h-full bg-[#F5F5F2]/[0.03]" />
        <div className="absolute top-0 left-[40%] w-px h-full bg-[#F5F5F2]/[0.03]" />
        <div className="absolute top-0 left-[60%] w-px h-full bg-[#F5F5F2]/[0.03]" />
        <div className="absolute top-0 left-[80%] w-px h-full bg-[#F5F5F2]/[0.03]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="font-body text-sm uppercase tracking-[0.25em] text-[#A7A55C] mb-8 animate-fade-in">
              India's Trusted Crypto Consultancy
            </p>
            <h1
              className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-[1.05] tracking-tight mb-8 animate-slide-up"
              data-testid="hero-heading"
            >
              From your first Bitcoin to your first blockchain product.
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed mb-10 max-w-xl animate-slide-up stagger-2">
              End-to-end crypto consultancy for individuals and businesses. Investment advisory, crypto tax planning, regulatory compliance, blockchain development. Everything under one roof.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up stagger-3">
              <Link
                to="/contact"
                className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-all duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2 hover:gap-3"
                data-testid="hero-cta-button"
              >
                Book a Free Consultation <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/individuals"
                className="bg-transparent text-[#F5F5F2] hover:bg-[#F5F5F2]/10 transition-all duration-300 font-body font-medium text-sm tracking-wide px-8 py-4 rounded-none border border-[#F5F5F2]/25"
                data-testid="hero-secondary-button"
              >
                Explore Our Services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="grid grid-cols-2 gap-5">
              {metrics.map((m) => (
                <div key={m.label} className="bg-[#F5F5F2]/[0.06] backdrop-blur-sm border border-[#F5F5F2]/[0.08] p-6 group hover:bg-[#F5F5F2]/[0.1] transition-colors duration-300">
                  <m.icon size={20} weight="duotone" className="text-[#A7A55C]/60 mb-3" />
                  <AnimatedCounter value={m.value} label={m.label} />
                  <p className="font-body text-xs text-[#F5F5F2]/50 mt-1 uppercase tracking-[0.1em]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricsMobile() {
  return (
    <section className="lg:hidden bg-[#21324D] border-t border-[#F5F5F2]/10" data-testid="metrics-mobile">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {metrics.map((m) => (
            <div key={m.label} className="text-center">
              <AnimatedCounter value={m.value} label={m.label} />
              <p className="font-body text-xs text-[#F5F5F2]/50 mt-1 uppercase tracking-[0.1em]">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="bg-white border-b border-[#383A46]/10 py-10 sm:py-12" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-8">
          {trustSignals.map((item) => (
            <div key={item.label} className="flex items-center gap-3 text-[#383A46]/50 hover:text-[#A7A55C] transition-colors duration-300">
              <item.icon size={22} weight="duotone" />
              <span className="font-body text-sm font-medium tracking-wide">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicePillars() {
  const allPillars = [
    ...individualServices.slice(0, 3).map(s => ({ ...s, type: "individual", linkBase: "/services/" })),
    ...businessServices.slice(0, 3).map(s => ({ ...s, type: "business", linkBase: "/services/" })),
  ];

  return (
    <section className="py-24 sm:py-32 bg-[#F5F5F2]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">What We Do</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
              One firm for everything crypto
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="font-body text-base text-[#383A46] leading-relaxed">
              Whether you are an individual investor navigating crypto markets and taxes, or a business building on blockchain, BitGenix is your single trusted partner.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allPillars.map((service) => (
            <Link
              to={`${service.linkBase}${service.slug}`}
              key={service.slug}
              className="group bg-white border border-[#383A46]/10 p-8 sm:p-10 rounded-none transition-all duration-300 hover:-translate-y-1 hover:border-[#A7A55C] hover:shadow-[0_16px_48px_-12px_rgba(33,50,77,0.12)]"
              data-testid={`service-card-${service.slug}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="bg-[#21324D] w-12 h-12 flex items-center justify-center">
                  <service.icon size={22} weight="duotone" className="text-[#A7A55C]" />
                </div>
                <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#383A46]/35 font-semibold mt-1">
                  {service.type === "individual" ? "Individual" : "Business"}
                </span>
              </div>
              <h3 className="font-body font-medium text-lg text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors">
                {service.title}
              </h3>
              <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-6">
                {service.shortDesc}
              </p>
              <span className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
                Learn more <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
        <div className="flex justify-center gap-8 mt-14">
          <Link
            to="/individuals"
            className="font-body font-medium text-sm text-[#21324D] hover:text-[#A7A55C] transition-colors inline-flex items-center gap-2 border-b border-transparent hover:border-[#A7A55C] pb-1"
            data-testid="view-individual-services"
          >
            All Individual Services <ArrowRight size={14} />
          </Link>
          <Link
            to="/businesses"
            className="font-body font-medium text-sm text-[#21324D] hover:text-[#A7A55C] transition-colors inline-flex items-center gap-2 border-b border-transparent hover:border-[#A7A55C] pb-1"
            data-testid="view-business-services"
          >
            All Business Services <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Process</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
              How we work with you
            </h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="font-body text-base text-[#383A46] leading-relaxed">
              Every engagement starts with understanding. We do not sell packages or push cookie-cutter solutions. We listen, design, and execute alongside you.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {howItWorks.map((item, i) => (
            <div key={item.step} className={`relative p-10 ${i < 2 ? 'md:border-r border-[#383A46]/10' : ''}`}>
              <span className="font-heading text-7xl font-semibold text-[#A7A55C]/10 leading-none block mb-6">
                {item.step}
              </span>
              <h3 className="font-body font-medium text-xl text-[#21324D] mb-4">
                {item.title}
              </h3>
              <p className="font-body text-sm text-[#383A46]/70 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-24 sm:py-32 bg-[#F5F5F2]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-4">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Client Stories</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight mb-6">
              Trusted by 140+ clients across India and globally
            </h2>
            <p className="font-body text-sm text-[#383A46]/70 leading-relaxed">
              From individual crypto investors to blockchain startups, our clients trust BitGenix for expert advisory that delivers results.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="bg-white border border-[#383A46]/10 p-10 sm:p-14 relative">
              <Quotes size={48} weight="fill" className="text-[#A7A55C]/10 absolute top-8 left-8" />
              <div className="relative min-h-[180px]">
                <p className="font-heading font-normal italic text-xl sm:text-2xl text-[#21324D] leading-relaxed mb-8">
                  "{testimonials[current].text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body font-semibold text-sm text-[#21324D]">
                      {testimonials[current].name}
                    </p>
                    <p className="font-body text-sm text-[#383A46]/50">
                      {testimonials[current].role}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setCurrent((current - 1 + testimonials.length) % testimonials.length)}
                      className="w-10 h-10 border border-[#383A46]/15 hover:border-[#A7A55C] text-[#383A46] hover:text-[#A7A55C] transition-all flex items-center justify-center"
                      data-testid="testimonial-prev"
                    >
                      <CaretLeft size={16} weight="bold" />
                    </button>
                    <button
                      onClick={() => setCurrent((current + 1) % testimonials.length)}
                      className="w-10 h-10 border border-[#383A46]/15 hover:border-[#A7A55C] text-[#383A46] hover:text-[#A7A55C] transition-all flex items-center justify-center"
                      data-testid="testimonial-next"
                    >
                      <CaretRight size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Dots */}
              <div className="flex gap-2 mt-8">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`h-1 transition-all duration-300 ${
                      i === current ? "bg-[#A7A55C] w-8" : "bg-[#383A46]/15 w-4"
                    }`}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegulationBanner() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="regulation-section">
      <div className="absolute inset-0 bg-[#152F2C]" />
      <div className="absolute inset-0 opacity-[0.07]">
        <img
          src="https://images.unsplash.com/photo-1773236376162-14b9c09b28f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGx1eHVyeSUyMGZpbmFuY2V8ZW58MHx8fHwxNzc0OTM5NjU0fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">India Crypto Regulation</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
              Crypto is legal in India. The rules are complex. We simplify them.
            </h2>
            <p className="font-body text-base text-[#F5F5F2]/65 leading-relaxed mb-8">
              India imposes a 30% tax on crypto gains, 1% TDS on every transaction, mandatory FIU-IND registration for service providers, and evolving SEBI oversight. Whether you are an investor managing tax obligations or a business seeking compliance, our crypto consultants navigate the complexity so you can focus on growth.
            </p>
            <Link
              to="/blog/india-crypto-tax-guide-section-115bbh-194s"
              className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-2 hover:gap-3 transition-all"
              data-testid="regulation-read-more"
            >
              Read our complete crypto tax guide <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {[
              { label: "Section 115BBH", desc: "30% flat tax on all crypto gains" },
              { label: "Section 194S", desc: "1% TDS on every crypto transaction" },
              { label: "FIU-IND", desc: "Mandatory registration for VDA service providers" },
              { label: "PMLA", desc: "Anti-money laundering compliance requirements" },
            ].map((item) => (
              <div key={item.label} className="border border-[#A7A55C]/20 bg-[#F5F5F2]/[0.04] p-6 hover:border-[#A7A55C]/40 transition-colors duration-300">
                <p className="font-body font-semibold text-sm text-[#A7A55C] mb-2">{item.label}</p>
                <p className="font-body text-sm text-[#F5F5F2]/55">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function LatestInsights() {
  const latestPosts = blogPosts.slice(0, 3);
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="latest-insights-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Latest Insights</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
              Expert crypto knowledge
            </h2>
          </div>
          <Link
            to="/blog"
            className="hidden sm:inline-flex font-body font-medium text-sm text-[#21324D] hover:text-[#A7A55C] transition-colors items-center gap-2"
            data-testid="view-all-blog"
          >
            View all articles <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestPosts.map((post) => (
            <Link
              to={`/blog/${post.slug}`}
              key={post.slug}
              className="group border border-[#383A46]/10 transition-all duration-300 hover:border-[#A7A55C]"
              data-testid={`insight-card-${post.slug}`}
            >
              <div className="bg-[#21324D] h-40 flex items-end p-6">
                <span className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium">
                  {post.category}
                </span>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-body font-medium text-base text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors leading-snug line-clamp-3">
                  {post.title}
                </h3>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-body text-xs text-[#383A46]/50">{post.readTime}</span>
                  <ArrowRight size={14} weight="bold" className="text-[#A7A55C] group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="sm:hidden mt-8 text-center">
          <Link
            to="/blog"
            className="font-body font-medium text-sm text-[#A7A55C] inline-flex items-center gap-2"
          >
            View all articles <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-24 sm:py-32 bg-[#21324D] relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#A7A55C]/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#A7A55C]/[0.02] rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Ready to take the next step in crypto?
          </h2>
          <p className="font-body text-base sm:text-lg text-[#F5F5F2]/65 max-w-xl mx-auto mb-10 leading-relaxed">
            Whether you are an individual looking for crypto investment guidance or a business navigating crypto regulation, start with a conversation. No obligations, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-all duration-300 font-body font-semibold text-sm tracking-wide px-10 py-4 rounded-none inline-flex items-center gap-2 hover:gap-3"
              data-testid="cta-book-button"
            >
              Book a Free Consultation <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/about"
              className="bg-transparent text-[#F5F5F2] hover:bg-[#F5F5F2]/10 transition-all duration-300 font-body font-medium text-sm tracking-wide px-10 py-4 rounded-none border border-[#F5F5F2]/25"
              data-testid="cta-about-button"
            >
              Learn About BitGenix
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Crypto Consultancy India | Investment Advisory, Tax Planning, Blockchain Development"
        description="BitGenix is India's trusted crypto consultancy for individuals and businesses. Expert advisory on crypto investment strategy, tax planning under Section 115BBH, regulatory compliance, FIU-IND registration, and blockchain development. 140+ clients advised, $36M+ in assets."
        path="/"
        keywords={[
          "crypto consultancy India",
          "crypto tax consultant India",
          "FIU-IND registration consultant",
          "blockchain consulting India",
          "crypto investment advisory India",
          "crypto compliance India",
          "NRI crypto tax consultant",
        ]}
        jsonLd={[organizationSchema(), websiteSchema()]}
      />
      <HeroSection />
      <MetricsMobile />
      <TrustSection />
      <ServicePillars />
      <HowItWorks />
      <Testimonials />
      <RegulationBanner />
      <LatestInsights />
      <CTASection />
    </div>
  );
}
